// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type { AuthnRequest, OidcRequestData } from '@imprivata-cloud/authn';
import type { CodingContext } from '@imprivata-cloud/dataprivacy';
import type { Option } from 'fp-ts/Option';
import type { ActionType } from 'typesafe-actions';
import type { Observable } from 'rxjs';
import type {
  getCredentials,
  processCapturedCredentials,
  processLoginResult,
  startApplicationAccess,
} from '../api/appAccessWebService';
import type {
  authenticationStatus$,
  endJourneyAndCloseWindow,
  sendAuthnDataToAgent,
  setAuthenticationStatus,
  showWindow,
} from '../agent-api';
import type * as actions from './actions';
import type { SessionDataKey } from '../agent-api/types';
import type { InternetConnectivityStatus } from '../constants';

export type Action = ActionType<typeof actions>;

export enum LearnCredentialsType {
  DESKTOP_ACCESS = 'DESKTOP_ACCESS',
  EPIC_HYPERDRIVE = 'EPIC_HYPERDRIVE',
}

export enum CredentialPromptKind {
  // backend learns the credentials, then hands them to the agent
  LEARN = 'LEARN',
  // raw credentials go straight to the agent
  DIRECT = 'DIRECT',
}

export type LearnCredentialPrompt = {
  kind: CredentialPromptKind.LEARN;
  isPasswordCaptureOnly: boolean;
  usernameHint: string;
  aadUpn: string;
  backendVerificationInProgress: boolean;
  agentVerificationInProgress: boolean;
};

export enum DirectPromptCause {
  // authn could not find the user; cancelling returns to authn
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  // Desktop Access is offline; there is no authn screen to cancel back to
  OFFLINE = 'OFFLINE',
}

export type UserNotFoundPrompt = {
  kind: CredentialPromptKind.DIRECT;
  cause: DirectPromptCause.USER_NOT_FOUND;
  // typed in authn before user-not-found; when known, only the password is asked
  username?: string;
};

export type OfflinePrompt = {
  kind: CredentialPromptKind.DIRECT;
  cause: DirectPromptCause.OFFLINE;
};

export type CredentialPrompt =
  | LearnCredentialPrompt
  | UserNotFoundPrompt
  | OfflinePrompt;

export type RootState = {
  error: actions.PartialBannerProps | null;
  appAccessContext: AppAccessContext | null;
  pkceData: OidcRequestData | null;
  authnData: Option<AuthnRequestContext> | null;
  // This is how UI component knows to show capture creds form
  uiCaptureCredentials: CredentialPrompt | null;
  agentSaml: string | null;
  networkConnectionStatus: InternetConnectivityStatus;
};

export type EpicDependencies = {
  authenticationStatus$: typeof authenticationStatus$;
  endJourneyAndCloseWindow: typeof endJourneyAndCloseWindow;
  showWindow: typeof showWindow;
  sendAuthnDataToAgent: typeof sendAuthnDataToAgent;
  setAuthenticationStatus: typeof setAuthenticationStatus;
  startApplicationAccess: typeof startApplicationAccess;
  getCredentials: typeof getCredentials;
  processCapturedCredentials: typeof processCapturedCredentials;
  processLoginResult: typeof processLoginResult;
};

export const toPromiseType = async <T>(
  observable: Observable<T>,
): Promise<T> => {
  return observable as unknown as Promise<T>;
};

export interface UiCaptureCredentialsActionData {
  isPasswordCaptureOnly: boolean;
  usernameHint: string;
  aadUpn: string;
  banner?: actions.PartialBannerProps;
}

export interface DirectCredentialsActionData {
  username: string;
  password: string;
}

export interface ProcessCapturedCredentialsActionData {
  codingContext: CodingContext;
  username: string;
  password: string;
  isPasswordCaptureOnly?: boolean;
}

export interface AppAccessContext extends Record<string, unknown> {
  username?: string;
  password?: string;
  domain?: string;
  usernameEditable?: boolean;
  // not in agent AppAccessContext :( - move out
  samlData?: string;
  // not in agent AppAccessContext :( - move out
  codingContext?: CodingContext;
  // not in agent AppAccessContext :( - move out
  isSamlAuthFlow?: boolean;

  // Epic Oauth Workflow
  baseUrl?: string;
  fhirServerUrl?: string;
  isProduction?: boolean;
}

export interface SamlData extends Record<string, unknown> {
  spEntityId?: string;
  audience?: string;
  recipient?: string;
  transactionId?: string;
  accepted?: boolean;
  required?: boolean;
}

export interface ReadSessionDataRequest {
  keys: SessionDataKey[];
  codingContext: CodingContext;
  identityToken: string;
}

export interface AuthnRequestContext extends AuthnRequest {
  samlData: string;
  factorData: string;
}
